import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Game, GameContract, ListResponseContract } from '@isophi/teachers-shared';
import { Observable, throwError } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { TimestampObservableCache } from '../utils/timestamp-observable-cache';

@Injectable({
  providedIn: 'root',
})
export class GameService {
  protected static readonly CACHE_EXPIRATION: number = 3600; // in seconds

  protected gamesCache: TimestampObservableCache<Game[]> | null = null;

  protected gameCache: { [key: string]: TimestampObservableCache<Game> } = {};

  public constructor(private http: HttpClient) {}

  public get games$(): Observable<Game[]> {
    const url = `${environment.teacherAPI}/scenarios/`;

    if (this.gamesCache === null || this.gamesCache.isExpired()) {
      const games$ = this.http.get<ListResponseContract<GameContract>>(url).pipe(
        map((response) => response.results.map((gameData) => Game.deserialize(gameData))),
        shareReplay(1),
        catchError((error) => {
          this.gamesCache = null;
          return throwError(() => error);
        })
      );

      this.gamesCache = new TimestampObservableCache<Game[]>(games$, GameService.CACHE_EXPIRATION);
    }

    return this.gamesCache.observable;
  }

  public getGame(uuid: string): Observable<Game> {
    const url = `${environment.teacherAPI}/scenarios/${uuid}/`;

    const gameCache = this.gameCache[uuid];
    if (gameCache === undefined || gameCache.isExpired()) {
      const game$ = this.http.get<GameContract>(url).pipe(
        map((gameData) => Game.deserialize(gameData)),
        shareReplay(1),
        catchError((error) => {
          delete this.gameCache[uuid];
          return throwError(() => error);
        })
      );
      this.gameCache[uuid] = new TimestampObservableCache<Game>(game$, GameService.CACHE_EXPIRATION);
    }

    return this.gameCache[uuid].observable;
  }

  /**
   * Invalidate groups cache.
   */
  public invalidateCache(): void {
    this.gamesCache = null;
    this.gameCache = {};
  }
}
